import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Util } from '../utils/util.service';
import { environment } from '../../environments/environment';
import { formatDate } from '@angular/common';
import { GlobalState } from '../global.state';
import { Auth } from '../auth.service';
import { UsersUtil } from '../utils/users.util';
import { Observable } from 'rxjs';


@Injectable()
export class MarketDataService {

  constructor( private _http: HttpClient, private _state: GlobalState, private _auth: Auth ) {
  }

  baseUrl: string = environment.ripsawAPIBaseUrl;

  getTreasuryRates( date?: Date ): Observable<any> {

    const numberOfObservations: string = this._state.globalVars.numberOfObservations ?? '60';

    let url: string = `${ this.baseUrl }/marketDataJS/treasuryRates?observations=${ numberOfObservations }`;

    if ( date ) {
      url = `${ url }&date=${ formatDate( date, 'yyyy-MM-dd', 'en-US' ) }`;
    }

    return this._http.get( url )
      .pipe(
        map( Util.extractData ),
        catchError( Util.handleError ),
      );
  }

  /**
   * Fetches the TIPS (Treasury Inflation-Protected Securities) rates from the market data service.
   *
   * @param {Date} [date] - Optional date parameter to get the TIPS rates for a specific date. If not provided, retrieves data for the latest available date.
   * @return {Observable<any>} An Observable containing the TIPS rates data or an error if the request fails.
   */
  getTIPSRates( date?: Date ): Observable<any> {
    let url: string = `${ this.baseUrl }/marketDataJS/tipsRates`;

    if ( date ) {
      url = `${ url }&date=${ formatDate( date, 'yyyy-MM-dd', 'en-US' ) }`;
    }

    return this._http.get( url )
      .pipe(
        map( Util.extractData ),
        catchError( Util.handleError ),
      );
  }

  /**
   * Fetches the risk returns for a list of securities from the market data service.
   * Uses HTTP POST request and processes the response with data extraction and error handling utilities.
   * @param securities - array of security objects
   * @param type - 'stocks' - when all funds are stocks | 'bond' - when all funds are bonds | 'mixed' - when funds are a mix of stock and bond
   */
  getRiskReturns( securities: any[], type: string ): Observable<any> {

    const prefs = {};
    if ( this._auth.authenticated() ) {
      const userPrefs: any = UsersUtil.checkPreferences( this._auth, 'marketInfo' );

      Object.keys( userPrefs?.marketInfo ).forEach( ( key: string ): void => {
        prefs[ key ] = userPrefs?.marketInfo?.[ key ]?.value;
      } );
    }
    const url: string = `${ this.baseUrl }/marketDataJS/riskReturns?type=${ type }`;

    return this._http.post( url, { securities, prefs } )
      .pipe(
        map( Util.extractData ),
        catchError( Util.handleError ),
      );

  }

  /**
   * Fetches historical VIX (Volatility Index) data from the API endpoint.
   * The endpoint retrieves daily data for a specified number of observations, along with computed statistics.
   * Uses HTTP GET request and processes the response with data extraction and error handling utilities.
   *
   * @return {Observable<any>} An Observable that emits the historical VIX data from the API or an error if the request fails.
   */
  getHistoricalVix(): Observable<any> {

    const numberOfObservations: string = this._state.globalVars.numberOfObservations ?? '60';
    const url: string = `${ this.baseUrl }/historical/daily?identifier=VIX&exchange=INDX&computeStatistics=true&observations=${ numberOfObservations }`;

    return this._http.get( url )
      .pipe(
        map( Util.extractData ),
        catchError( Util.handleError ),
      );
  }
}
